import { DocumentThread, ThreadStorage, ThreadManager } from '../types/documentThread';

class DocumentThreadService implements ThreadManager {
  private static instance: DocumentThreadService;
  private readonly BASE_STORAGE_KEY = 'documentThreads';
  private readonly SESSION_KEY = 'currentSessionId';
  private readonly STORAGE_VERSION = '1.0.0';
  private readonly MAX_THREADS = 50; // Limit to prevent memory issues
  
  // Get tab-specific storage key
  private getStorageKey(): string {
    const sessionId = this.getCurrentSessionId();
    return `${this.BASE_STORAGE_KEY}_${sessionId}`;
  }
  
  private constructor() {}
  
  public static getInstance(): DocumentThreadService {
    if (!DocumentThreadService.instance) {
      DocumentThreadService.instance = new DocumentThreadService();
    }
    return DocumentThreadService.instance;
  }
  
  private getStorage(): ThreadStorage {
    try {
      const storageKey = this.getStorageKey();
      const stored = localStorage.getItem(storageKey);
      const currentSessionId = this.getCurrentSessionId();
      
      if (!stored) {
        console.log('📝 DocumentThreadService: Creating new tab-specific storage for:', storageKey);
        return this.createEmptyStorage();
      }
      
      const parsed = JSON.parse(stored);
      
      // Version check and migration if needed
      if (parsed.version !== this.STORAGE_VERSION) {
        console.log('🔄 DocumentThreadService: Migrating storage version');
        return this.createEmptyStorage();
      }
      
      // Since we're using tab-specific storage keys, session ID should always match
      // But we'll update it just in case
      if (parsed.sessionId !== currentSessionId) {
        console.log('🔄 DocumentThreadService: Updating session ID in tab storage');
        parsed.sessionId = currentSessionId;
      }
      
      // Convert date strings back to Date objects
      Object.values(parsed.threads).forEach((thread: any) => {
        thread.lastUpdated = new Date(thread.lastUpdated);
        thread.createdAt = new Date(thread.createdAt);
        thread.messages.forEach((msg: any) => {
          if (msg.timestamp) {
            msg.timestamp = new Date(msg.timestamp);
          }
        });
      });
      
      console.log('📖 DocumentThreadService: Loaded tab-specific storage:', {
        storageKey,
        threadCount: Object.keys(parsed.threads).length,
        sessionId: parsed.sessionId
      });
      
      return parsed;
    } catch (error) {
      console.error('DocumentThreadService: Error reading storage:', error);
      return this.createEmptyStorage();
    }
  }
  
  private saveStorage(storage: ThreadStorage): void {
    try {
      const storageKey = this.getStorageKey();
      
      // Cleanup old threads if we exceed the limit
      const threadEntries = Object.entries(storage.threads);
      if (threadEntries.length > this.MAX_THREADS) {
        // Sort by lastUpdated and keep only the most recent
        const sortedThreads = threadEntries.sort(
          ([,a], [,b]) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime()
        );
        
        storage.threads = Object.fromEntries(
          sortedThreads.slice(0, this.MAX_THREADS)
        );
        
        console.log(`🧹 DocumentThreadService: Cleaned up old threads, keeping ${this.MAX_THREADS} most recent`);
      }
      
      localStorage.setItem(storageKey, JSON.stringify(storage));
      console.log('💾 DocumentThreadService: Saved to tab-specific storage:', storageKey);
    } catch (error) {
      console.error('DocumentThreadService: Error saving storage:', error);
      // If storage is full, try to clear some space
      if (error instanceof Error && error.name === 'QuotaExceededError') {
        this.clearOldThreads();
        try {
          const storageKey = this.getStorageKey();
          localStorage.setItem(storageKey, JSON.stringify(storage));
        } catch (retryError) {
          console.error('DocumentThreadService: Failed to save even after cleanup:', retryError);
        }
      }
    }
  }
  
  private createEmptyStorage(): ThreadStorage {
    return {
      version: this.STORAGE_VERSION,
      sessionId: this.getCurrentSessionId(),
      threads: {},
      lastActiveDocument: null
    };
  }
  
  private createEmptyThread(documentId: string, documentName: string): DocumentThread {
    const now = new Date();
    return {
      documentId,
      documentName,
      messages: [],
      conversationId: undefined,
      lastMessageId: undefined,
      responseContent: '',
      streamingContent: '',
      isStreaming: false,
      error: null,
      finalTime: null,
      uploadedFiles: [],
      lastUpdated: now,
      createdAt: now
    };
  }
  
  public getThread(documentId: string, documentName: string): DocumentThread {
    const storage = this.getStorage();
    
    if (!storage.threads[documentId]) {
      console.log(`📝 DocumentThreadService: Creating new thread for document: ${documentId}`);
      storage.threads[documentId] = this.createEmptyThread(documentId, documentName);
      this.saveStorage(storage);
    } else {
      console.log(`📖 DocumentThreadService: Loading existing thread for document: ${documentId}`);
      // Update document name if it has changed
      if (storage.threads[documentId].documentName !== documentName) {
        storage.threads[documentId].documentName = documentName;
        storage.threads[documentId].lastUpdated = new Date();
        this.saveStorage(storage);
      }
    }
    
    return storage.threads[documentId];
  }
  
  public updateThread(documentId: string, updates: Partial<DocumentThread>): void {
    const storage = this.getStorage();
    
    if (!storage.threads[documentId]) {
      console.warn(`DocumentThreadService: Thread ${documentId} not found for update`);
      return;
    }
    
    // Update the thread with new data
    const thread = storage.threads[documentId];
    Object.assign(thread, updates, {
      lastUpdated: new Date()
    });
    
    console.log(`💾 DocumentThreadService: Updated thread for document: ${documentId}`, {
      messagesCount: thread.messages.length,
      responseLength: thread.responseContent.length,
      isStreaming: thread.isStreaming
    });
    
    this.saveStorage(storage);
  }
  
  public clearThread(documentId: string): void {
    const storage = this.getStorage();
    
    if (!storage.threads[documentId]) {
      return;
    }
    
    const documentName = storage.threads[documentId].documentName;
    storage.threads[documentId] = this.createEmptyThread(documentId, documentName);
    
    console.log(`🗑️ DocumentThreadService: Cleared thread for document: ${documentId}`);
    this.saveStorage(storage);
  }
  
  public clearAllThreads(): void {
    const storageKey = this.getStorageKey();
    console.log('🗑️ DocumentThreadService: Clearing all threads for current tab:', storageKey);
    const storage = this.createEmptyStorage();
    this.saveStorage(storage);
  }
  
  public hasThread(documentId: string): boolean {
    const storage = this.getStorage();
    const hasThread = !!storage.threads[documentId];
    const hasContent = hasThread && (
      storage.threads[documentId].messages.length > 0 || 
      storage.threads[documentId].responseContent.length > 0
    );
    return hasContent;
  }
  
  public getAllThreads(): Record<string, DocumentThread> {
    const storage = this.getStorage();
    return storage.threads;
  }
  
  public setActiveDocument(documentId: string): void {
    const storage = this.getStorage();
    storage.lastActiveDocument = documentId;
    this.saveStorage(storage);
  }
  
  public getActiveDocument(): string | null {
    const storage = this.getStorage();
    return storage.lastActiveDocument;
  }
  
  private clearOldThreads(): void {
    // Clean up current tab's old threads
    const storage = this.getStorage();
    const now = new Date();
    const threeDaysAgo = new Date(now.getTime() - (3 * 24 * 60 * 60 * 1000));
    
    // Remove threads older than 3 days
    const threadEntries = Object.entries(storage.threads);
    let removedCount = 0;
    
    threadEntries.forEach(([id, thread]) => {
      if (new Date(thread.lastUpdated) < threeDaysAgo) {
        delete storage.threads[id];
        removedCount++;
      }
    });
    
    if (removedCount > 0) {
      console.log(`🧹 DocumentThreadService: Removed ${removedCount} old threads from current tab`);
      this.saveStorage(storage);
    }
    
    // Also clean up old tab-specific storage entries from localStorage
    this.cleanupOldTabStorage();
  }
  
  private cleanupOldTabStorage(): void {
    try {
      const now = Date.now();
      const sevenDaysAgo = now - (7 * 24 * 60 * 60 * 1000); // 7 days
      let cleanedCount = 0;
      
      // Iterate through all localStorage keys
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.BASE_STORAGE_KEY)) {
          try {
            // Extract timestamp from the session ID in the key
            const parts = key.split('_');
            if (parts.length >= 3) {
              const timestamp = parseInt(parts[2]); // userId_timestamp_randomId format
              if (timestamp && timestamp < sevenDaysAgo) {
                localStorage.removeItem(key);
                cleanedCount++;
                console.log(`🧹 Removed old tab storage: ${key}`);
              }
            }
          } catch (parseError) {
            console.warn('Could not parse storage key for cleanup:', key);
          }
        }
      }
      
      if (cleanedCount > 0) {
        console.log(`🧹 DocumentThreadService: Cleaned up ${cleanedCount} old tab storage entries`);
      }
    } catch (error) {
      console.error('Error during tab storage cleanup:', error);
    }
  }
  
  // Get memory usage statistics
  public getMemoryStats(): { threadCount: number; totalSize: number; oldestThread: Date | null } {
    const storage = this.getStorage();
    const threads = Object.values(storage.threads);
    
    const stats = {
      threadCount: threads.length,
      totalSize: JSON.stringify(storage).length,
      oldestThread: null as Date | null
    };
    
    if (threads.length > 0) {
      stats.oldestThread = threads.reduce((oldest, thread) => {
        const threadDate = new Date(thread.createdAt);
        return !oldest || threadDate < oldest ? threadDate : oldest;
      }, null as Date | null);
    }
    
    return stats;
  }
  
  // Session management methods
  public startNewSession(): void {
    // Generate a new session ID that includes user info and timestamp for uniqueness
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substring(2, 15);
    
    // Get user info from localStorage/auth token if available
    let userId = 'anonymous';
    try {
      const authToken = localStorage.getItem('authToken');
      if (authToken) {
        // Extract user info from token (you might need to adjust this based on your token structure)
        const payload = JSON.parse(atob(authToken.split('.')[1]));
        userId = payload.sub || payload.id || payload.username || 'user';
      }
    } catch (error) {
      console.warn('Could not extract user ID from auth token:', error);
    }
    
    const newSessionId = `${userId}_${timestamp}_${randomId}`;
    
    console.log('🔄 DocumentThreadService: Starting new session for current tab:', newSessionId);
    sessionStorage.setItem(this.SESSION_KEY, newSessionId);
    
    // Clear all existing threads for the new session (current tab only)
    this.clearAllThreads();
    
    // Run cleanup to remove old tab storage entries
    this.cleanupOldTabStorage();
  }
  
  public getCurrentSessionId(): string {
    let sessionId = sessionStorage.getItem(this.SESSION_KEY);
    
    if (!sessionId) {
      // Generate initial session ID
      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(2, 15);
      
      // Get user info from localStorage/auth token if available
      let userId = 'anonymous';
      try {
        const authToken = localStorage.getItem('authToken');
        if (authToken) {
          const payload = JSON.parse(atob(authToken.split('.')[1]));
          userId = payload.sub || payload.id || payload.username || 'user';
        }
      } catch (error) {
        console.warn('Could not extract user ID from auth token:', error);
      }
      
      sessionId = `${userId}_${timestamp}_${randomId}`;
      sessionStorage.setItem(this.SESSION_KEY, sessionId);
      console.log('🆕 DocumentThreadService: Created new session:', sessionId);
    }
    
    return sessionId;
  }
}

export default DocumentThreadService;